import { useEffect, useState } from 'react'
import { IconContext } from 'react-icons'
import { QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import { useStaticLicense } from 'hooks/license'
import { AppProps as NextAppProps } from 'next/app'
import ExpiredLicense from 'pages/components/ExpiredLicense'
import { ThemeProvider } from 'theme'
import ROUTES from 'constants/routes'
import { LoadingSpinner } from 'components/feedback/loading/loading-spinner'
import { captureCSPViolations } from 'utils/csp'
import { queryClient } from 'utils/queryClient'
import { initializeAnalytics } from 'utils/segment'
import { Fonts } from '../assets/fonts'

interface AppProps extends NextAppProps {
  err: Error // Workaround for https://github.com/vercel/next.js/issues/8592
}

const ICON_CONTEXT_VALUE = { size: '24px' }

if (typeof window !== 'undefined') {
  captureCSPViolations()
}

const AppComponent = ({ Component, pageProps, err }: AppProps) => {
  const { data: license, isLoading } = useStaticLicense()

  if (isLoading) {
    return <LoadingSpinner h="100vh" />
  }

  return license?.isValid ? (
    <Component {...pageProps} err={err} />
  ) : (
    <ExpiredLicense />
  )
}

function App(props: AppProps) {
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
    initializeAnalytics()
  }, [])

  const { router } = props

  if (router?.pathname === ROUTES.UNSUPPORTED_BROWSER) {
    // Don't render any of the app code if the browser is unsupported
    const { Component, pageProps, err } = props
    return (
      <>
        <Fonts />
        <Component {...pageProps} err={err} />
      </>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Fonts />
        <IconContext.Provider value={ICON_CONTEXT_VALUE}>
          <div>
            {isClient ? (
              <>
                <AppComponent {...props} />
                <ReactQueryDevtools position="bottom-right" />
              </>
            ) : null}
          </div>
        </IconContext.Provider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
